import { Field } from 'react-final-form';
import styled from 'styled-components/macro';
import * as validators from 'validators';
import { CalculatorFormValues } from 'common/types';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { combineValidators } from 'validators/combineValidators';
import * as formFormatters from '@net0-calc/common/form-utils/formatters';
import * as formParsers from '@net0-calc/common/form-utils/parsers';
import { HomeIcon, CarIcon, MapIcon, PlaneIcon } from '@net0-calc/common/assets/svg';
import { Input, StepDescription, StepTitle } from '@net0-calc/common/components/ui';
import { WizardPageProps } from '@net0-calc/common/types';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';

const translationPrefix = 'events.pages.travelAndAccommodation';

const InputCard = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <InputCardRoot>
    <InputCardIcon>{icon}</InputCardIcon>
    <InputCardContent>{children}</InputCardContent>
  </InputCardRoot>
);

export const TravelAndAccommodationPage: FC<WizardPageProps<CalculatorFormValues>> = () => {
  const { t } = useTranslation();

  const fields: { name: string; label: string; icon: ReactNode; unit: string }[] = [
    {
      name: 'livingLocallyPeopleCount',
      label: t(translationPrefix + '.form.field.livingLocallyPeopleCount.label'),
      icon: <HomeIcon stroke="var(--color1)" />,
      unit: 'people',
    },
    {
      name: 'travelingWithinRegionPeopleCount',
      label: t(translationPrefix + '.form.field.travelingWithinRegionPeopleCount.label'),
      icon: <CarIcon stroke="var(--color1)" />,
      unit: 'people',
    },
    {
      name: 'travelingWithinCountryPeopleCount',
      label: t(translationPrefix + '.form.field.travelingWithinCountryPeopleCount.label'),
      icon: <MapIcon stroke="var(--color1)" />,
      unit: 'people',
    },
    {
      name: 'travelingFromAnotherCountryPeopleCount',
      label: t(translationPrefix + '.form.field.travelingFromAnotherCountryPeopleCount.label'),
      icon: <PlaneIcon stroke="var(--color1)" />,
      unit: 'people',
    },
  ];

  return (
    <Root>
      <StepTitle>{t(translationPrefix + '.title')}</StepTitle>
      <StepDescription>{t(translationPrefix + '.subTitle')}</StepDescription>

      <CardsGrid>
        {fields.map(({ name, label, icon, unit }) => (
          <Field
            key={name}
            name={name}
            type="number"
            pattern="[0-9]*"
            parse={formParsers.toNumber}
            format={formFormatters.numberToString}
            validate={combineValidators(validators.isNumber, validators.isNegativeNumber)}>
            {field => (
              <InputCard icon={icon}>
                <CardInput label={label} placeholder="0" unit={unit} {...field} />
              </InputCard>
            )}
          </Field>
        ))}
      </CardsGrid>
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  margin-bottom: 60px;

  @media ${DEVICE.laptop} {
    margin-bottom: 70px;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media ${DEVICE.laptop} {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const InputCardRoot = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  background: var(--color14);
  border: 1px solid var(--color14);
  border-radius: 16px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: var(--color10);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  &:focus-within {
    border-color: var(--color2);
    box-shadow: 0 0 0 3px rgba(206, 17, 38, 0.06);
  }

  @media ${DEVICE.laptop} {
    padding: 20px;
    gap: 16px;
  }
`;

const InputCardIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color5);
  border-radius: 12px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;

  & svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    overflow: visible;
  }
`;

const InputCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardInput = styled(Input)`
  white-space: nowrap;

  & > label {
    ${TYPOGRAPHY.captionRegular12}
    color: var(--color9);
    margin-bottom: 6px;
  }

  & > div {
    height: 44px;
    border-radius: 10px;
    margin-bottom: 0;
    background: var(--color5);

    @media ${DEVICE.laptop} {
      margin-bottom: 0;
    }
  }
`;
